import { useState } from "react";
import { useTranslation } from "react-i18next";
import { jod } from "@astroplanet/shared";
import { hrApi, useAdjustments, useAdjustmentTypes } from "../../hooks/hr";
import { useLocale } from "../../hooks/useLocale";
import { localName, money } from "../../lib/format";
import type { Adjustment, HrLevel } from "../../lib/typesHr";
import { Empty, SectionHeader } from "../ui/Badges";
import { Modal } from "../ui/Modal";
import { dateOnly, EmployeeSelect, Field, monthFirstIso, ReasonDialog, StatusPill, Table, useAction, useEmpName } from "./shared";

function AdjustmentDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const types = useAdjustmentTypes();
  const { act, busy } = useAction();
  const [employeeId, setEmployeeId] = useState("");
  const [typeId, setTypeId] = useState("");
  const [amount, setAmount] = useState("");
  const [month, setMonth] = useState(monthFirstIso(0));
  const [reason, setReason] = useState("");
  const type = (types.data ?? []).find((x) => x.id === typeId);
  const fils = jod(Number(amount) || 0);
  const over = type?.max_amount_fils != null && fils > type.max_amount_fils;
  const valid = !!employeeId && !!typeId && fils > 0 && !over && reason.trim().length >= 3 && month.endsWith("-01");
  async function submit() {
    const r = await act(() => hrApi.post("/adjustments", { employeeId, typeId, amountFils: fils, reason: reason.trim(), effectiveMonth: month }), "hr.adj.sent");
    if (r) { onClose(); setAmount(""); setReason(""); }
  }
  return (
    <Modal open={open} title={t("hr.adj.new")} onClose={onClose} footer={<><button type="button" className="btn-ghost" onClick={onClose}>{t("common.cancel")}</button><button type="button" className="btn-primary" disabled={!valid || busy} onClick={() => void submit()}>{t("hr.leave.submit")}</button></>}>
      <Field label={t("hr.employee")}><EmployeeSelect value={employeeId} onChange={setEmployeeId} /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label={t("hr.adj.type")}>
          <select className="field" value={typeId} onChange={(e) => { setTypeId(e.target.value); const d = (types.data ?? []).find((x) => x.id === e.target.value)?.default_amount_fils; if (d) setAmount(money(d)); }}>
            <option value="">—</option>
            {(types.data ?? []).filter((x) => x.active).map((x) => <option key={x.id} value={x.id}>{t(`hr.adjKind.${x.kind}`)} · {localName(x, locale)}</option>)}
          </select>
        </Field>
        <Field label={`${t("hr.loans.amount")} (${t("currency")})`} hint={over ? t("hr.adj.aboveMax", { max: money(type!.max_amount_fils!) }) : undefined}><input className={`field num ${over ? "!border-red-400" : ""}`} inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} /></Field>
        <Field label={t("hr.adj.month")}><input type="date" className="field num" value={month} onChange={(e) => setMonth(e.target.value)} /></Field>
      </div>
      <Field label={t("hr.reason")}><textarea className="field min-h-16" value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
      <p className="text-[10px] font-semibold text-gray-500">{t("hr.adj.help")}</p>
    </Modal>
  );
}

function TypeDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const { act, busy } = useAction();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [nameAr, setNameAr] = useState("");
  const [kind, setKind] = useState<"bonus" | "penalty">("bonus");
  const [max, setMax] = useState("");
  async function submit() {
    const r = await act(() => hrApi.post("/adjustment-types", { code: code.trim().toUpperCase(), name: name.trim(), nameAr: nameAr.trim() || null, kind, maxAmountFils: max ? jod(Number(max)) : null }), "hr.saved");
    if (r) { onClose(); setCode(""); setName(""); setNameAr(""); setMax(""); }
  }
  return (
    <Modal open={open} title={t("hr.adj.newType")} onClose={onClose} footer={<><button type="button" className="btn-ghost" onClick={onClose}>{t("common.cancel")}</button><button type="button" className="btn-primary" disabled={busy || !code.trim() || !name.trim()} onClick={() => void submit()}>{t("hr.save")}</button></>}>
      <div className="grid grid-cols-2 gap-3">
        <Field label={t("hr.code")}><input className="field num" value={code} onChange={(e) => setCode(e.target.value)} /></Field>
        <Field label={t("hr.adj.kind")}><select className="field" value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}><option value="bonus">{t("hr.adjKind.bonus")}</option><option value="penalty">{t("hr.adjKind.penalty")}</option></select></Field>
        <Field label={t("hr.name")}><input className="field" value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <Field label={t("hr.nameAr")}><input className="field" value={nameAr} onChange={(e) => setNameAr(e.target.value)} /></Field>
        <Field label={`${t("hr.adj.max")} (${t("currency")})`}><input className="field num" inputMode="decimal" value={max} onChange={(e) => setMax(e.target.value)} /></Field>
      </div>
    </Modal>
  );
}

export function AdjustmentsTable({ rows, level }: { rows: Adjustment[]; level: HrLevel }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const name = useEmpName();
  const { act, busy } = useAction();
  const [rejecting, setRejecting] = useState<Adjustment | null>(null);
  const types = useAdjustmentTypes(level >= 2);
  const decide = (a: Adjustment, decision: "approve" | "reject", note?: string) => act(() => hrApi.post("/decide", { entityType: "adjustment", entityId: a.id, decision, note }), decision === "approve" ? "hr.approved" : "hr.rejected");
  const reverse = (a: Adjustment) => {
    const mirror = (types.data ?? []).find((x) => x.kind !== a.kind && x.active);
    if (!mirror) return;
    void act(() => hrApi.post(`/adjustments/${a.id}/reverse`, { reason: t("hr.adj.reversalReason"), mirrorTypeId: mirror.id }), "hr.done");
  };
  return (
    <>
      <Table head={[t("hr.employee"), t("hr.adj.type"), t("hr.loans.amount"), t("hr.adj.month"), t("hr.status.label"), ""]} empty={rows.length === 0 ? <Empty>{t("hr.adj.none")}</Empty> : undefined}>
        {rows.map((a) => (
          <tr key={a.id}>
            <td className="font-black">{name(a)}</td>
            <td><span className={a.kind === "bonus" ? "text-emerald-700" : "text-red-700"}>{t(`hr.adjKind.${a.kind}`)}</span> · {localName({ name: a.type_name, nameAr: a.type_name_ar }, locale)}<div className="text-[10px] text-gray-500">{a.reason}</div></td>
            <td className={`num font-bold ${a.kind === "bonus" ? "text-emerald-700" : "text-red-700"}`}>{a.kind === "bonus" ? "+" : "−"}{money(a.amount_fils)}</td>
            <td className="num">{dateOnly(a.effective_month, locale)}</td>
            <td><StatusPill status={a.status} />{a.status === "pending" && a.waiting_for && <div className="mt-1 text-[10px] font-semibold text-gray-400">{t("hr.waitingFor", { role: t(`hr.role.${a.waiting_for}`, { defaultValue: a.waiting_for }) })}</div>}</td>
            <td className="whitespace-nowrap text-end">
              {a.status === "pending" && level >= 1 && (<><button type="button" className="btn-success me-1" disabled={busy} onClick={() => void decide(a, "approve")}>{t("hr.approve")}</button><button type="button" className="btn-ghost text-red-700" disabled={busy} onClick={() => setRejecting(a)}>{t("hr.reject")}</button></>)}
              {a.status === "applied" && level >= 2 && <button type="button" className="btn-ghost" disabled={busy} onClick={() => reverse(a)}>{t("hr.adj.reverse")}</button>}
              {["pending", "approved"].includes(a.status) && level >= 1 && <button type="button" className="btn-ghost ms-1" disabled={busy} onClick={() => void act(() => hrApi.post(`/adjustments/${a.id}/cancel`), "hr.done")}>{t("hr.cancel")}</button>}
            </td>
          </tr>
        ))}
      </Table>
      <ReasonDialog open={!!rejecting} title={t("hr.rejectTitle")} confirmLabel={t("hr.reject")} onClose={() => setRejecting(null)} onConfirm={(note) => { const a = rejecting!; setRejecting(null); void decide(a, "reject", note); }} />
    </>
  );
}

export function AdjustmentsPanel({ level }: { level: HrLevel }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState("");
  const list = useAdjustments({ status: status || undefined });
  const [open, setOpen] = useState(false);
  const [typeOpen, setTypeOpen] = useState(false);
  return (
    <section className="card space-y-3 p-5" aria-label={t("hr.tabs.adjustments")}>
      <SectionHeader icon="🏅" title={t("hr.tabs.adjustments")} subtitle={t("hr.adj.subtitle")}
        right={<div className="flex flex-wrap items-center gap-2">
          <select className="field !w-auto" value={status} onChange={(e) => setStatus(e.target.value)} aria-label={t("hr.status.label")}>
            <option value="">{t("hr.all")}</option>
            {["pending", "approved", "applied", "rejected", "cancelled", "reversed"].map((s) => <option key={s} value={s}>{t(`hr.status.${s}`)}</option>)}
          </select>
          {level >= 2 && <button type="button" className="btn-ghost" onClick={() => setTypeOpen(true)}>{t("hr.adj.newType")}</button>}
          <button type="button" className="btn-primary" onClick={() => setOpen(true)}>{t("hr.adj.new")}</button>
        </div>} />
      <AdjustmentsTable rows={list.data ?? []} level={level} />
      <AdjustmentDialog open={open} onClose={() => setOpen(false)} />
      <TypeDialog open={typeOpen} onClose={() => setTypeOpen(false)} />
    </section>
  );
}
